import { useEffect, useMemo, useState, type ReactNode } from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import sharePointIcon from "@/assets/sharepoint-icon.png"
import type {
  GraphItem,
  SharePointSource,
  SyncSelection,
} from "../../domain/entities/sharepoint"
import { useSharePointAuth } from "../hooks/use-sharepoint-auth"
import {
  useSharePointChildren,
  useSharePointChildrenAllFetcher,
  useSharePointOneDrive,
  useSharePointSearch,
  useSharePointShared,
  useSharePointSiteDrives,
  useSharePointSites,
} from "../hooks/use-sharepoint-browse"
import { useSharePointSync, type SyncJob } from "../hooks/use-sharepoint-sync"
import {
  buildDisplayPath,
  isSelectable,
  selectAllVisible,
  selKey,
  toggleSelection,
  unselectVisible,
} from "./sharepoint-file-list"
import { Breadcrumb } from "./sharepoint-breadcrumb"
import { BrowseList } from "./sharepoint-browse-list"
import { SelectedPane } from "./sharepoint-selected-pane"
import { SyncProgressSection } from "./sharepoint-sync-progress"
import { cn } from "@/lib/utils"

type Props = { open: boolean; kbId: string; onClose: () => void }

type Crumb = { driveId: string; itemId: string; label: string }

const SOURCE_LABELS: Record<SharePointSource, string> = {
  shared: "Shared with me",
  onedrive: "My OneDrive",
  sites: "SharePoint sites",
}

const BrowsePane = ({
  loading,
  empty,
  children,
}: {
  loading: boolean
  empty?: boolean
  children: ReactNode
}) => (
  <div className="flex-1 overflow-y-auto rounded border">
    {loading ? (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    ) : empty ? (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No files here.
      </div>
    ) : (
      children
    )}
  </div>
)

const SiteRow = ({
  connectionId,
  siteId,
  name,
  onPickDrive,
}: {
  connectionId: string
  siteId: string
  name: string
  onPickDrive: (driveId: string, driveName: string) => void
}) => {
  const [expanded, setExpanded] = useState(false)
  const drivesQ = useSharePointSiteDrives(connectionId, expanded ? siteId : null)
  return (
    <div className="border-b">
      <button
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-muted"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="shrink-0 text-base leading-none">🏢</span>
        <span className="truncate">{name}</span>
      </button>
      {expanded ? (
        <div className="pl-6">
          {drivesQ.isFetching ? (
            <div className="py-2 pl-3">
              <Spinner className="size-4" />
            </div>
          ) : (
            (drivesQ.data ?? []).map((d) => (
              <button
                key={d.driveId}
                className="flex w-full cursor-pointer items-center gap-3 px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => onPickDrive(d.driveId, d.name)}
              >
                <span className="shrink-0 text-base leading-none">🗂️</span>
                <span className="truncate">{d.name}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

export const SharePointSyncDialog = ({ open, kbId, onClose }: Props) => {
  const { connect, connectionId, isConnecting, error, clearConnection } =
    useSharePointAuth(kbId)
  const [source, setSource] = useState<SharePointSource>("shared")
  const [crumbs, setCrumbs] = useState<Crumb[]>([])
  const [selected, setSelected] = useState<Map<string, SyncSelection>>(new Map())
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectAllBusy, setSelectAllBusy] = useState(false)
  const sync = useSharePointSync(kbId)
  const fetchAllChildren = useSharePointChildrenAllFetcher(connectionId)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const cursor = crumbs[crumbs.length - 1] ?? null
  const atSitesRoot = source === "sites" && !cursor
  const query = debouncedSearch.trim().toLowerCase()
  // Inside a folder we search server-side (recursive Graph search). At a source
  // root there is no drive/item to scope a backend search to, so we filter the
  // already-loaded listing client-side by name instead.
  const searching = !!cursor && query.length > 0
  const rootFiltering = !cursor && query.length > 0

  const sharedQ = useSharePointShared(connectionId, source === "shared" && !cursor)
  const oneDriveQ = useSharePointOneDrive(connectionId, source === "onedrive" && !cursor)
  const sitesQ = useSharePointSites(connectionId, atSitesRoot)
  const childrenQ = useSharePointChildren(
    connectionId,
    cursor?.driveId ?? null,
    cursor?.itemId ?? null,
  )
  const searchQ = useSharePointSearch(
    connectionId,
    cursor?.driveId ?? null,
    cursor?.itemId ?? null,
    debouncedSearch,
  )

  const items: GraphItem[] = useMemo(() => {
    if (searching) return searchQ.data ?? []
    if (cursor) return childrenQ.data ?? []
    let base: GraphItem[] = []
    if (source === "shared") base = sharedQ.data ?? []
    else if (source === "onedrive") base = oneDriveQ.data ?? []
    if (rootFiltering) return base.filter((i) => i.name.toLowerCase().includes(query))
    return base
  }, [searching, searchQ.data, cursor, source, childrenQ.data, sharedQ.data, oneDriveQ.data, rootFiltering, query])

  const sites = useMemo(() => {
    const all = sitesQ.data ?? []
    if (rootFiltering) return all.filter((s) => s.name.toLowerCase().includes(query))
    return all
  }, [sitesQ.data, rootFiltering, query])

  const isLoading =
    (searching && searchQ.isFetching) ||
    childrenQ.isFetching ||
    sharedQ.isFetching ||
    oneDriveQ.isFetching ||
    sitesQ.isFetching

  // A reused connection may have expired on the backend (Redis TTL ~1h): the
  // active pane's browse call then errors. Derive that here and fall back to
  // the connect screen (the user re-authenticates via the button).
  const paneError = searching
    ? searchQ.error
    : cursor
      ? childrenQ.error
      : source === "shared"
        ? sharedQ.error
        : source === "onedrive"
          ? oneDriveQ.error
          : sitesQ.error
  const connectionStale = !!connectionId && !!paneError

  const crumbLabels = crumbs.map((c) => c.label)
  const pathFor = (item: GraphItem) => buildDisplayPath(crumbLabels, item.name)

  const openFolder = (item: GraphItem) => {
    setCrumbs((c) => [...c, { driveId: item.driveId, itemId: item.id, label: item.name }])
    setSearch("")
    setDebouncedSearch("")
  }

  const switchSource = (s: SharePointSource) => {
    setSource(s)
    setCrumbs([])
    setSearch("")
    setDebouncedSearch("")
  }

  const enterSiteDrive = (driveId: string, driveName: string) => {
    setCrumbs([{ driveId, itemId: "root", label: driveName }])
    setSearch("")
    setDebouncedSearch("")
  }

  const navigateTo = (index: number) => {
    setCrumbs((c) => c.slice(0, index + 1))
    setSearch("")
    setDebouncedSearch("")
  }
  const goBack = () => {
    setCrumbs((c) => c.slice(0, -1))
    setSearch("")
    setDebouncedSearch("")
  }

  const toggle = (item: GraphItem) =>
    setSelected((s) => toggleSelection(s, item, pathFor(item)))

  const allVisibleSelected =
    items.filter(isSelectable).every((i) => selected.has(selKey(i.driveId, i.id))) &&
    items.some(isSelectable)
  const selectAllActive = allVisibleSelected

  // Select-all toggles every permitted file individually so each stays
  // removable. Inside a folder it fetches *all* pages (not just the visible
  // first page) via the backend; at a source root it acts on the listing.
  const onSelectAll = async () => {
    if (cursor && !searching) {
      setSelectAllBusy(true)
      try {
        const all = await fetchAllChildren(cursor.driveId, cursor.itemId)
        setSelected((s) =>
          allVisibleSelected ? unselectVisible(s, all) : selectAllVisible(s, all, pathFor),
        )
      } catch {
        toast.error("Không tải được danh sách file trong thư mục.")
      } finally {
        setSelectAllBusy(false)
      }
      return
    }
    setSelected((s) =>
      allVisibleSelected ? unselectVisible(s, items) : selectAllVisible(s, items, pathFor),
    )
  }

  const removeSelected = (key: string) =>
    setSelected((s) => {
      const next = new Map(s)
      next.delete(key)
      return next
    })

  const handleClose = () => {
    // Start the close animation first, then clear the transient *browse* state
    // *after* it has played (DialogContent fades out in ~100ms). Resetting
    // synchronously would flip the content mid-animation, causing a visible
    // resize jump. Sync jobs are NOT cleared here — they live in sessionStorage
    // and their progress section reappears on reopen. The SharePoint connection
    // also stays cached so reopening skips the Microsoft login.
    onClose()
    window.setTimeout(() => {
      setSelected(new Map())
      setCrumbs([])
      setSearch("")
      setDebouncedSearch("")
    }, 200)
  }

  const handleSync = () => {
    if (!connectionId || selected.size === 0) return
    const count = selected.size
    sync.start({ connectionId, items: [...selected.values()] })
    // Clear the selection so the pane is ready for the next batch; the sync
    // runs in its own progress section below.
    setSelected(new Map())
    toast.info(`Đang đồng bộ ${count} mục…`)
  }

  const retryJob = (job: SyncJob) => {
    if (!connectionId) return
    // Resend the retryable failures of the latest attempt (the retry tail).
    const tailStatus = job.retry ? job.retry.status : job.status
    const retryItems: SyncSelection[] = (tailStatus?.failed ?? [])
      .filter((f) => f.retryable)
      .map((f) => ({
        driveId: f.driveId,
        itemId: f.itemId,
        name: f.name,
        size: 0,
        isFolder: f.isFolder,
        path: f.folder ? `${f.folder}/${f.name}` : f.name,
      }))
    if (retryItems.length === 0) return
    sync.retry(job.retryTargetId, { connectionId, items: retryItems })
    toast.info(`Thử lại ${retryItems.length} mục…`)
  }

  // Shrink the panes when the progress section is present so both fit without
  // scrolling the whole dialog.
  const hasJobs = sync.jobs.length > 0
  const paneHeight = hasJobs ? "h-[42vh]" : "h-[55vh]"

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="w-[72vw] max-w-[1100px] sm:max-w-[1100px]">
        <DialogHeader>
          <DialogTitle>Sync from SharePoint</DialogTitle>
        </DialogHeader>

        {!connectionId || connectionStale ? (
          <div className="flex flex-col items-center gap-4 py-10">
            <img
              src={sharePointIcon}
              alt="SharePoint"
              className="h-[200px] w-[200px] object-contain"
            />
            <p className="text-sm text-muted-foreground">
              Login Microsoft to browse your SharePoint files.
            </p>
            {connectionStale ? (
              <p className="text-sm text-amber-600">
                Phiên SharePoint đã hết hạn. Vui lòng kết nối lại.
              </p>
            ) : null}
            <Button
              onClick={() => {
                clearConnection()
                connect().catch(() => {})
              }}
              disabled={isConnecting}
            >
              {isConnecting ? <Spinner className="size-4" /> : null}
              Connect Microsoft
            </Button>
            {error ? <p className="text-sm text-red-500">{error}</p> : null}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            {/* LEFT: browse */}
            <div className={cn("flex flex-col gap-2", paneHeight)}>
              <div className="flex gap-2">
                {(["shared", "onedrive", "sites"] as SharePointSource[]).map((s) => (
                  <Button
                    key={s}
                    variant={source === s ? "default" : "outline"}
                    size="sm"
                    className="cursor-pointer"
                    onClick={() => switchSource(s)}
                  >
                    {SOURCE_LABELS[s]}
                  </Button>
                ))}
              </div>

              {crumbs.length > 0 ? (
                <Breadcrumb labels={crumbLabels} onNavigate={navigateTo} onBack={goBack} />
              ) : null}

              <div className="flex items-center gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={cursor ? "Search in this folder…" : "Search…"}
                  className="h-8"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 cursor-pointer"
                  onClick={onSelectAll}
                  disabled={selectAllBusy || (!cursor && !items.some(isSelectable))}
                >
                  {selectAllBusy ? <Spinner className="size-4" /> : null}
                  {selectAllActive ? "Unselect all" : "Select all"}
                </Button>
              </div>

              {atSitesRoot ? (
                <BrowsePane loading={sitesQ.isFetching} empty={sites.length === 0}>
                  {sites.map((site) => (
                    <SiteRow
                      key={site.id}
                      connectionId={connectionId}
                      siteId={site.id}
                      name={site.name}
                      onPickDrive={enterSiteDrive}
                    />
                  ))}
                </BrowsePane>
              ) : (
                <BrowsePane loading={isLoading} empty={items.length === 0}>
                  <BrowseList
                    items={items}
                    selectedIds={new Set(selected.keys())}
                    onOpenFolder={openFolder}
                    onToggle={toggle}
                  />
                </BrowsePane>
              )}
            </div>

            {/* RIGHT: selection (always available, even while syncing) */}
            <SelectedPane
              entries={[...selected.values()]}
              heightClass={paneHeight}
              onRemove={removeSelected}
              onClear={() => setSelected(new Map())}
              onSync={handleSync}
              syncDisabled={selected.size === 0 || sync.starting}
            />
          </div>

          {hasJobs ? (
            <SyncProgressSection
              jobs={sync.jobs}
              onRetry={retryJob}
              onDismiss={sync.dismiss}
            />
          ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
